<template>
  <div id="app">
    <el-container direction="vertical" class="app-container" v-if="showHeader">
      <!-- 顶部导航栏 (全局) -->
      <el-header class="page-header" height="80px">
        <div class="logo-area">
          <img
            src="@/assets/yanding-logo.png"
            alt="YANDING Logo"
            class="logo-img"
            @click="goToHome"
          />
        </div>
        <div class="header-menu">
          <!-- RIQA 下载 -->
          <a :href="riqaDownloadUrl" class="header-link" target="_blank">
            <img
              src="@/assets/riqa-logo.png"
              alt="RIQA Logo"
              class="riqa-logo-in-link"
            />
            <span class="header-link-text">{{
              $t("header.riqaDownload")
            }}</span>
          </a>
          <!-- 商城 -->
          <a
            :href="yandingShopUrl"
            class="header-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="@/assets/yanding-shop-logo.png"
              alt="YANDING Shop Logo"
              class="riqa-logo-in-link-shop"
            />
            <span class="header-link-text">{{ $t("header.yandingWeb") }}</span>
          </a>

          <!-- “帮助”下拉菜单 -->
          <el-dropdown trigger="click" @command="handleHelpCommand">
            <span class="el-dropdown-link header-link">
              <i class="el-icon-question"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="feedback">{{
                $t("header.feedback")
              }}</el-dropdown-item>
              <el-dropdown-item command="Instructions">{{
                $t("header.Instructions")
              }}</el-dropdown-item>
              <!-- 语言切换 -->
              <div
                class="el-dropdown-menu__item el-dropdown-menu__item--divided"
                @click.stop
              >
                <el-dropdown
                  trigger="hover"
                  placement="right"
                  @command="handleLanguageSwitch"
                  style="width: 100%"
                >
                  <span class="el-dropdown-link language-switch-trigger">
                    <span>{{ $t("header.languageSwitch") }}</span>
                    <span class="language-globe-icon">🌐</span>
                  </span>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item
                      v-for="lang in availableLanguages"
                      :key="lang.code"
                      :command="lang.code"
                    >
                      {{ $t(`header.${lang.key}`) }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </el-dropdown-menu>
          </el-dropdown>

          <el-dropdown
            ref="notificationDropdown"
            trigger="manual"
            placement="bottom-end"
          >
            <span
              class="el-dropdown-link"
              @click.stop="handleNotificationClick"
            >
              <el-badge
                :value="unreadCount"
                :max="99"
                class="notification-badge"
                :hidden="unreadCount === 0"
              >
                <i class="el-icon-bell header-link"></i>
              </el-badge>
            </span>
            <el-dropdown-menu
              slot="dropdown"
              class="message-dropdown-menu"
              style="width: 360px; padding: 0"
            >
              <div class="message-header">
                <span>{{ $t("news.title") }}</span>
              </div>
              <el-scrollbar class="message-list-scrollbar">
                <el-empty
                  v-if="!messages.length"
                  class="message-empty-state"
                  :image-size="60"
                  :description="$t('news.no_messages')"
                ></el-empty>
                <ul v-else class="message-list">
                  <li
                    v-for="msg in messages"
                    :key="msg.id"
                    class="message-item"
                    :class="{ 'is-read': msg.is_read }"
                    @click="handleMessageClick(msg)"
                  >
                    {{ msg.message.title }}
                  </li>
                </ul>
              </el-scrollbar>
              <div class="message-footer" @click="handleViewAll">
                {{ $t("news.view_all") }}
              </div>
            </el-dropdown-menu>
          </el-dropdown>

          <!-- 用户头像与登录 -->
          <el-dropdown trigger="click" @command="handleUserCommand">
            <span class="el-dropdown-link">
              <el-avatar
                size="medium"
                :src="
                  isLoggedIn
                    ? require('@/assets/user_login.png')
                    : require('@/assets/user_nologin.png')
                "
              ></el-avatar>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item v-if="isLoggedIn" command="logout">{{
                $t("header.logout")
              }}</el-dropdown-item>
              <el-dropdown-item v-else command="login">{{
                $t("header.login")
              }}</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </el-header>

      <el-main class="main-view-container">
        <router-view @request-login="showLoginModal" />
      </el-main>
    </el-container>

    <router-view v-else class="full-page-view" />

    <auth-modal
      :visible.sync="authModalVisible"
      @login-success="onLoginSuccess"
    ></auth-modal>
    <feedback-modal :visible.sync="feedbackModalVisible"></feedback-modal>

    <all-messages-modal
      :visible.sync="allMessagesModalVisible"
      :messages="messages"
      :initial-message-id="selectedMessageId"
      @mark-as-read="handleMarkAsRead"
    ></all-messages-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  getLanguageSetting,
  getUnreadMessageCount,
  getUserMessages,
} from "@/api";

import AuthModal from "@/components/AuthModal.vue";
import FeedbackModal from "@/components/FeedbackModal.vue";
import AllMessagesModal from "@/components/AllMessagesModal.vue";

export default {
  name: "App",
  components: {
    AuthModal,
    FeedbackModal,
    AllMessagesModal,
  },
  computed: {
    ...mapGetters(["isLoggedIn"]),

    showHeader() {
      return this.$route.meta.showHeader !== false;
    },
    // 计算属性来动态生成商城链接
    yandingShopUrl() {
      // 直接使用响应式的 this.$i18n.locale
      const displayLang = this.$i18n.locale;
      if (displayLang === "zh") {
        return "https://yanding.com/site";
      } else {
        return "https://yanding.com/en/site";
      }
    },
    riqaDownloadUrl() {
      // 直接使用响应式的 this.$i18n.locale
      const displayLang = this.$i18n.locale;
      if (displayLang === "zh") {
        return "https://yanding.com/product/detail?id=1894";
      } else {
        return "https://yanding.com/en/product/detail?id=1894";
      }
    },
    // 获取可用的语言列表（排除当前语言）
    availableLanguages() {
      const currentLang = this.$i18n.locale;
      const allLangs = [
        { code: "zh", key: "chinese" },
        { code: "ja", key: "japanese" },
        { code: "en", key: "english" },
      ];
      // 过滤掉当前语言
      return allLangs.filter((lang) => lang.code !== currentLang);
    },
  },
  data() {
    return {
      authModalVisible: false,
      feedbackModalVisible: false,
      unreadCount: 0,
      messages: [],
      allMessagesModalVisible: false,
      selectedMessageId: null,
    };
  },
  watch: {
    isLoggedIn: {
      handler(isLoggedIn) {
        if (isLoggedIn) {
          this.fetchUnreadCount();
          this.fetchUserMessages();
        } else {
          this.unreadCount = 0;
          this.messages = [];
        }
      },
      immediate: true,
    },
  },
  created() {
    this.initializeLanguage();
  },
  methods: {
    onLoginSuccess(data) {
      if (data && data.token) {
        this.$store.dispatch("loginSuccess", {
          token: data.token,
          userInfo: data,
        });
      } else {
        console.error(
          "Login success event did not receive a valid token. The `dispatch` was not called."
        );
      }

      this.authModalVisible = false;
    },

    handleUserCommand(command) {
      if (command === "login") {
        this.authModalVisible = true;
      } else if (command === "logout") {
        this.$store.dispatch("logout");
        // this.$notify.success({
        //   title: this.$t("success.successTitle"),
        //   message: this.$t("success.logoutSuccess"),
        // });
        if (this.$route.path !== "/") {
          this.$router.push("/");
        }
      }
    },

    handleNotificationClick() {
      if (!this.isLoggedIn) {
        this.authModalVisible = true;
      } else {
        this.fetchUserMessages();
        this.$refs.notificationDropdown.show();
      }
    },

    handleMarkAsRead(messageId) {
      const message = this.messages.find((msg) => msg.id === messageId);
      if (message && !message.is_read) {
        message.is_read = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      }
    },
    showLoginModal() {
      this.authModalVisible = true;
    },
    goToHome() {
      this.$router.go(0);
    },
    async fetchUnreadCount() {
      if (!this.isLoggedIn) return;
      try {
        const response = await getUnreadMessageCount();
        if (response.success && response.data) {
          this.unreadCount = response.data.count;
        }
      } catch (error) {
        console.log("获取未读消息数失败。", error);
        this.unreadCount = 0;
      }
    },
    async fetchUserMessages() {
      if (!this.isLoggedIn) return;
      try {
        const response = await getUserMessages();
        if (response.success && Array.isArray(response.data)) {
          this.messages = response.data;
        } else {
          this.messages = [];
        }
      } catch (error) {
        console.log("获取消息列表失败。", error);
        this.messages = [];
      }
    },
    async initializeLanguage() {
      const displayLang = localStorage.getItem("display_lang");
      const controlLang = localStorage.getItem("control_lang");

      try {
        const response = await getLanguageSetting();
        let serverLang = null;

        if (response && response.data && response.data.lang) {
          serverLang = response.data.lang;
        }

        // 设置控制语言：如果后端返回了语言，总是使用后端返回的（控制语言固定跟随后端）
        // 如果后端没有返回，则使用已保存的，最后默认为 "zh"
        const finalControlLang = serverLang || controlLang || "zh";
        // 如果有后端返回，或者需要初始化，都更新 store
        if (serverLang || !controlLang || finalControlLang !== controlLang) {
          this.$store.commit("SET_CONTROL_LANG", finalControlLang);
        }

        // 设置显示语言：优先使用已保存的显示语言，如果没有则使用后端返回的，最后默认为 "zh"
        const finalDisplayLang = displayLang || serverLang || "zh";
        this.$i18n.locale = finalDisplayLang;
        if (finalDisplayLang !== displayLang) {
          localStorage.setItem("display_lang", finalDisplayLang);
        }
      } catch (error) {
        console.error("Failed to fetch language setting.", error);
        // 错误处理：使用已保存的语言或默认值
        const finalControlLang = controlLang || "zh";
        const finalDisplayLang = displayLang || "zh";

        // 只有在需要初始化时才更新（避免频繁更新）
        if (!controlLang) {
          this.$store.commit("SET_CONTROL_LANG", finalControlLang);
        }

        this.$i18n.locale = finalDisplayLang;
        if (!displayLang) {
          localStorage.setItem("display_lang", finalDisplayLang);
        }
      }
    },
    handleHelpCommand(command) {
      if (command === "feedback") {
        if (this.isLoggedIn) {
          this.feedbackModalVisible = true;
        } else {
          this.authModalVisible = true;
        }
      } else if (command === "Instructions") {
        const displayLang =
          localStorage.getItem("display_lang") || this.$i18n.locale;
        const apiUrl = process.env.VUE_APP_API_URL;
        let pdfPath = "";

        switch (displayLang) {
          case "zh":
            pdfPath = `${apiUrl}/api/v1/static/pdf/manual_cn.pdf`;
            break;
          case "ja":
            pdfPath = `${apiUrl}/api/v1/static/pdf/manual_jp.pdf`;
            break;
          case "en":
          default:
            pdfPath = `${apiUrl}/api/v1/static/pdf/manual_en.pdf`;
            break;
        }

        window.open(pdfPath, "_blank");
      }
    },
    handleLanguageSwitch(lang) {
      // 设置显示语言
      localStorage.setItem("display_lang", lang);
      this.$i18n.locale = lang;
    },
    handleMessageClick(message) {
      this.selectedMessageId = message.id;
      this.allMessagesModalVisible = true;
    },
    handleViewAll() {
      if (this.messages.length > 0) {
        this.selectedMessageId = this.messages[0].id;
      } else {
        this.selectedMessageId = null;
      }
      this.allMessagesModalVisible = true;
    },
  },
  // mounted() {
  //   document.addEventListener(
  //     "wheel",
  //     (event) => {
  //       if (event.ctrlKey || event.metaKey) {
  //         event.preventDefault();
  //       }
  //     },
  //     { passive: false }
  //   );
  //   document.addEventListener("keydown", (event) => {
  //     if (
  //       (event.ctrlKey || event.metaKey) &&
  //       (event.key === "+" ||
  //         event.key === "-" ||
  //         event.key === "0" ||
  //         event.keyCode === 187 ||
  //         event.keyCode === 189 ||
  //         event.keyCode === 48)
  //     ) {
  //       event.preventDefault();
  //     }
  //   });
  // },
  beforeDestroy() {
    document.removeEventListener("wheel", (event) => {
      if (event.ctrlKey || event.metaKey) event.preventDefault();
    });
    document.removeEventListener("keydown", (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === "+" || event.key === "-" || event.key === "0")
      )
        event.preventDefault();
    });
  },
};
</script>

<style>
html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: hidden;
}
#app {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
}
.app-container {
  height: 100vh;
}
.full-page-view {
  height: 100vh;
  width: 100vw;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e6ebf5;
  padding: 0 40px;
  flex-shrink: 0;
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 1000;
}
.logo-area .logo-img {
  height: 66px;
  vertical-align: middle;
  cursor: pointer;
}
.header-menu {
  display: flex;
  align-items: center;
  gap: 50px;
}
.header-link,
.el-dropdown-link {
  color: #606266;
  text-decoration: none;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  cursor: pointer;
}
.header-link i {
  font-size: 18px;
}
.el-icon-bell {
  font-size: 18px;
}
.riqa-logo-in-link {
  height: 33px;
  vertical-align: middle;
}
.riqa-logo-in-link-shop {
  height: 25px;
  vertical-align: middle;
}
.header-link-text {
  line-height: 22px;
}
.notification-badge {
  line-height: 1;
}
.el-dropdown-link {
  gap: 0;
}
.language-switch-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-left: -20px;
  margin-right: -20px;
  padding-left: 20px;
  padding-right: 20px;
}
.language-globe-icon {
  margin-left: 10px;
  font-size: 16px;
  display: inline-block;
  line-height: 1;
  vertical-align: middle;
}
.main-view-container {
  flex-grow: 1;
  overflow: auto;
  background-color: #f7f8fa;
}
.el-dropdown-menu__item {
  font-weight: bold;
}
.el-dropdown-menu {
  padding: 6px 6px;
}
.message-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 600;
}
.message-header::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 16px;
  right: 16px;
  height: 1px;
  background-color: #ebeef5;
}
.message-header .el-icon-delete {
  cursor: pointer;
  font-size: 18px;
  color: #909399;
}
.message-header .el-icon-delete:hover {
  color: #f56c6c;
}
.message-list-scrollbar {
  height: 130px;
}
.message-empty-state {
  padding: 20px 0;
}
.message-empty-state .el-empty__description {
  margin-top: 10px;
  font-size: 14px;
}
.message-list-scrollbar .el-scrollbar__wrap {
  overflow-x: hidden;
}
.message-list {
  list-style: none;
  padding: 6px 16px 6px 10px;
  margin: 0;
}
.message-item {
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
  line-height: 1.4;
  transition: background-color 0.2s;
  border-radius: 4px;
  margin-bottom: 4px;
  font-weight: 500;
  color: #303133;
}
.message-item.is-read {
  font-weight: normal;
  color: #909399;
}
.message-item:last-child {
  margin-bottom: 0;
}
.message-item:hover {
  background-color: #ecf5ff;
  color: #00a0e9;
}
.message-footer {
  text-align: center;
  padding: 12px 16px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  border-top: 1px solid #ebeef5;
}
.message-footer:hover {
  color: #00a0e9;
}
.el-notification {
  z-index: 9999 !important;
}
el-message {
  z-index: 9999 !important;
}
@media (max-width: 768px) {
  body {
    overflow: auto;
  }
  .page-header {
    padding: 0 15px;
    height: 60px !important;
  }
  .logo-area .logo-img {
    height: 45px;
  }
  .header-menu {
    gap: 20px;
  }
  .header-link-text {
    display: none;
  }
  .header-link {
    gap: 0;
  }
  .riqa-logo-in-link {
    height: 33px;
  }
  .header-link i {
    font-size: 22px;
  }
  .el-avatar--medium {
    width: 32px;
    height: 32px;
    line-height: 32px;
  }
}
.my-custom-loading .el-loading-spinner .el-loading-text {
  color: #00a0e9;
  font-size: 16px;
}
.my-custom-loading .el-loading-spinner .el-icon-loading {
  color: #00a0e9;
  font-size: 30px;
}
.el-notification__content {
  text-align: left !important;
}
</style>
<style>
@media (max-width: 768px) {
  .responsive-confirm-box {
    width: 90%;
    max-width: 400px;
  }
}
</style>
